import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Shield, Mail, Lock, User, ArrowRight } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions and Privacy Policy to continue');
      return;
    }

    setLoading(true);
    const result = await register(email, password, name);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#36454F]">
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <Shield className="w-14 h-14 mb-8 text-[#E0BFB8]" strokeWidth={1.5} />
          <h1 className="font-['Playfair_Display'] text-4xl font-semibold mb-6 text-center tracking-tight">
            Selah Slate&trade;
          </h1>
          <div className="max-w-sm text-center">
            <p className="text-base text-white/80 font-['Manrope'] leading-relaxed mb-8">
              Your past doesn't define your legacy. Financial "curses" end here. We've built a path to help you clear the record and start fresh.
            </p>
            <div className="space-y-3 text-left mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#E0BFB8] flex-shrink-0"></div>
                <span className="text-sm text-white/70 font-['Manrope']">Stop the cycle of errors.</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#E0BFB8] flex-shrink-0"></div>
                <span className="text-sm text-white/70 font-['Manrope']">Restore your financial identity.</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#E0BFB8] flex-shrink-0"></div>
                <span className="text-sm text-white/70 font-['Manrope']">Rebuild a foundation that lasts.</span>
              </div>
            </div>
            <div className="border-t border-white/10 pt-6">
              <p className="font-['Playfair_Display'] text-sm italic text-[#E0BFB8] leading-relaxed">
                "I am doing a new thing; now it springs forth, do you not perceive it?"
              </p>
              <p className="text-xs text-white/40 font-['Manrope'] mt-2">— Isaiah 43:19</p>
            </div>

            {/* Is / Is not */}
            <div className="border-t border-white/10 pt-6 space-y-2.5 text-left" data-testid="register-is-isnt">
              <p className="text-[10px] uppercase tracking-wider text-[#E0BFB8] font-['Manrope'] font-semibold">
                A word about what we are
              </p>
              <p className="text-xs text-white/75 font-['Manrope'] leading-relaxed">
                Selah Slate is an <strong className="text-white">all-in-one, free, faith-based companion</strong> that
                helps you analyze your credit report, draft FCRA dispute letters, track bureau
                responses, sync a shared household budget in real time, guide CFPB escalations, and
                plan debt payoff — grounded in scripture.
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#E0BFB8] font-['Manrope'] font-semibold pt-1">
                What we are not — and what we don't do
              </p>
              <p className="text-xs text-white/60 font-['Manrope'] leading-relaxed">
                We are <em>not</em> a credit-repair company, law firm, or financial advisor.
                We don't mail letters for you or contact bureaus on your behalf — you stay
                in control. We never sell your data or show ads.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F5F5F5]">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <Shield className="w-10 h-10 text-[#B76E79] mr-3" strokeWidth={1.5} />
            <span className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F]">
              Selah Slate&trade;
            </span>
          </div>

          <div className="bg-white p-8 border border-[#36454F]/15">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F] mb-2">
              Create your account
            </h2>
            <p className="text-[#36454F]/65 mb-5 font-['Manrope']">
              Start disputing credit errors for free
            </p>

            {/* Why free forever */}
            <div
              className="mb-5 p-4 rounded-sm bg-[#4CAF50]/5 border border-[#4CAF50]/20 space-y-2"
              data-testid="why-free-forever"
            >
              <div className="flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 rounded-full bg-[#4CAF50]/15 text-[#4CAF50] text-[9px] uppercase tracking-wider font-['Manrope'] font-bold">
                  Free — forever
                </span>
              </div>
              <p className="text-xs text-[#36454F]/80 font-['Manrope'] leading-relaxed">
                The Fair Credit Reporting Act gives every consumer the right to dispute
                errors for <strong className="text-[#36454F]">no cost</strong> — yet
                credit-repair companies charge families $75–$150 a month to do it for them.
                <strong className="text-[#36454F]"> No ads,
                no premium tiers, no data sales — ever.</strong>
              </p>
            </div>

            {/* Mobile-only: A word about what we are + verse */}
            <div className="lg:hidden mb-6 p-4 rounded-sm bg-[#36454F]/5 border border-[#36454F]/10 space-y-3" data-testid="register-mobile-is-isnt">
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-semibold">
                  A word about what we are
                </p>
                <p className="text-xs text-[#36454F]/80 font-['Manrope'] leading-relaxed">
                  Selah Slate is an <strong className="text-[#36454F]">all-in-one, free, faith-based companion</strong> that
                  helps you analyze your credit report, draft FCRA dispute letters, track bureau
                  responses, sync a shared household budget in real time, guide CFPB escalations, and
                  plan debt payoff — grounded in scripture.
                </p>
                <p className="text-[10px] uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-semibold pt-1">
                  What we are not — and what we don't do
                </p>
                <p className="text-xs text-[#36454F]/65 font-['Manrope'] leading-relaxed">
                  We are <em>not</em> a credit-repair company, law firm, or financial advisor.
                  We don't mail letters for you or contact bureaus on your behalf — you stay
                  in control. We never sell your data or show ads.
                </p>
              </div>
              <div className="pt-3 border-t border-[#36454F]/10">
                <p className="font-['Playfair_Display'] text-sm italic text-[#B76E79] leading-relaxed">
                  "I am doing a new thing; now it springs forth, do you not perceive it?"
                </p>
                <p className="text-[11px] text-[#36454F]/50 font-['Manrope'] mt-1">— Isaiah 43:19</p>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-[#D9534F]/10 border border-[#D9534F]/30 text-[#D9534F] text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#36454F] font-['Manrope'] font-medium">
                  Full Legal Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                    required
                    autoComplete="name"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#36454F] font-['Manrope'] font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                    required
                    autoComplete="email"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#36454F] font-['Manrope'] font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                    required
                    autoComplete="new-password"
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#36454F] font-['Manrope'] font-medium">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#36454F]/40" strokeWidth={1.5} />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                    required
                    autoComplete="new-password"
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !acceptedTerms}
                className="w-full h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Clear Your Slate
                    <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                  </span>
                )}
              </Button>

              <div className="flex items-start gap-2.5 pt-1" data-testid="terms-consent-wrap">
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={(v) => setAcceptedTerms(v === true)}
                  className="mt-0.5 border-[#36454F]/30 data-[state=checked]:bg-[#B76E79] data-[state=checked]:border-[#B76E79]"
                  data-testid="accept-terms-checkbox"
                />
                <Label
                  htmlFor="accept-terms"
                  className="text-xs text-[#36454F]/70 font-['Manrope'] leading-relaxed cursor-pointer select-none"
                >
                  I have read and accept the{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#B76E79] hover:underline font-medium" data-testid="link-terms">
                    Terms and Conditions
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#B76E79] hover:underline font-medium" data-testid="link-privacy">
                    Privacy Policy
                  </Link>
                  .
                </Label>
              </div>
            </form>

            <p className="mt-8 text-center text-[#36454F]/65 font-['Manrope']">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-[#B76E79] hover:text-[#9e5c66] font-medium transition-colors"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>

          <p className="mt-6 text-center text-[10px] text-[#36454F]/40 font-['Manrope'] max-w-md mx-auto leading-relaxed">
            Selah Slate is a financial wellness initiative of Selah and Stone Legacy Holdings LLC. All services are provided free of charge as part of our community legacy mission.
          </p>
        </div>
      </div>
    </div>
  );
}
